// src/pages/b2b/B2BHomePage.js - B2B용 홈 페이지

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import {
  faArrowLeft,
  faArrowRight,
  faChevronRight,
  faComments,
  faFlask,
  faLayerGroup,
  faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../context/AppContext';
import { usePageHeader } from '../../context/PageHeaderContext';
import B2BLayout from '../../components/b2b/B2BLayout';
import AnnouncementModal from '../../components/AnnouncementModal';
import NewStudyModal from '../../components/NewStudyModal';
import ArtifactEntryModal from '../../components/ArtifactEntryModal';
import { getB2BMembershipUsage } from '../../services/api';
import {
  PersonaTagChip,
  formatPersonaTagLabel,
  normalizePersonaTag,
} from '../../features/persona/components/PersonaShared';
import {
  personaAbTestApi,
  personaApi,
  personaAssetUrl,
  personaFigmaApi,
  personaInterviewApi,
  personaTestApi,
} from '../../features/persona/services/personaService';
import researchPlanIcon from '../../assets/icons/special/Ico_ Research Plan.svg';
import screeningSurveyIcon from '../../assets/icons/special/Ico_Screening survey.svg';
import guidelineIcon from '../../assets/icons/special/Ico_Guideline.svg';
import './B2BHomePage.scss';

const validationMethods = [
  {
    id: 'screen',
    title: '화면 사용성 검증',
    description: '퍼소나의 시각에서 UI의 문제점을 진단하고 사용성을 검증합니다.',
    icon: faLayerGroup,
    tone: 'orange',
    featured: true,
    path: '/tests/new?type=ui',
  },
  {
    id: 'ab',
    title: 'A/B 비교 검증',
    description: '시안의 A/B 비교검증을 통해 퍼소나가 선호하는 최적의 안을 제안합니다.',
    icon: faFlask,
    tone: 'blue',
    path: '/tests/new?type=ab',
  },
  {
    id: 'interview',
    title: '1:1 AI 인터뷰를 통한 인사이트',
    description: '타겟 퍼소나와의 심층 인터뷰를 진행하고 핵심 인사이트를 도출합니다.',
    icon: faComments,
    tone: 'violet',
    path: '/tests/interview/new',
  },
];

const HOME_PERSONA_LIMIT = 6;
const HOME_PERSONA_FETCH_LIMIT = 12;
const RECENT_RESEARCH_LIMIT = 5;
const PERSONA_PAGE_SIZE = 3;
const DISPLAY_TYPE_INTERVIEW = 'UX테스트 > 1:1 AI 인터뷰';

const researchCreateMethods = [
  {
    id: 'plan',
    title: '계획서 만들기',
    description: '리서치 목표와 방법을 담은 계획서를 생성합니다.',
    icon: researchPlanIcon,
  },
  {
    id: 'survey',
    title: '스크리닝 설문 만들기',
    description: '계획·관련 문서 또는 조사 목적을 바탕으로 스크리닝 설문을 만듭니다.',
    icon: screeningSurveyIcon,
  },
  {
    id: 'guideline',
    title: '조사 가이드라인 만들기',
    description: '계획·관련 문서 또는 조사 목적을 바탕으로 가이드라인을 만듭니다.',
    icon: guidelineIcon,
  },
];

const INDIVIDUAL_ANNOUNCEMENT_HIDDEN_KEY = 'reops_individual_announcement_popup_hidden';
const INDIVIDUAL_ANNOUNCEMENT_SHOWN_KEY = 'reops_individual_announcement_popup_shown';

const getTestScopeLabel = (test) => {
  const scopeType = test?.scope_type ?? test?.scopeType;
  const mode = test?.mode;
  if (scopeType === 'flow' || mode === 'flow') return '사용자 플로우';
  return '단일 화면';
};

const getRowDisplayType = (test, testType) => {
  if (testType === 'interview') return test?.typeLabel || DISPLAY_TYPE_INTERVIEW;
  if (testType === 'ab') return `A/B테스트 > ${getTestScopeLabel(test)}`;
  return `화면 사용성 검증 > ${getTestScopeLabel(test)}`;
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getThisMonthRange = () => {
  const now = new Date();
  return {
    start_date: formatDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1)),
    end_date: formatDateInputValue(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return Number(value || 0).toLocaleString('ko-KR');
};

const formatTestDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const normalizeStatus = (status) => {
  if (status === 'completed' || status === 'completed_with_errors') return 'completed';
  if (status === 'failed') return 'failed';
  if (status === 'running' || status === 'pending' || status === 'draft') return 'running';
  return status || 'running';
};

const testStatusLabel = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'completed') return '완료';
  if (normalized === 'failed') return '실패';
  if (normalized === 'running') return '생성 중';
  return status || '-';
};

const getRowTestType = (test) => {
  const kind = test?.kind || test?.testType || test?.type;
  if (kind === 'ab-test' || kind === 'ab') return 'ab';
  if (kind === 'interview') return 'interview';
  return 'ui';
};

const getInitials = (name = '') => {
  const normalized = String(name || '').trim();
  if (!normalized) return 'AI';
  const chars = Array.from(normalized.replace(/\s+/g, ''));
  return chars.slice(0, 2).join('').toUpperCase();
};

const isExcludedHomePersonaTag = (tag) => {
  const compact = String(tag || '').replace(/[\s_-]/g, '');
  const normalized = String(tag || '').trim().toLowerCase();
  return (
    (compact.includes('낮은') && /멤버[십쉽]활용도/.test(compact)) ||
    normalized === 'segment_based'
  );
};

const isDefaultPersonaFolder = (folder) => Boolean(folder?.is_default ?? folder?.isDefault);

const getPersonaFolderId = (persona) => persona?.folder_id ?? persona?.folderId ?? persona?.folder?.id ?? '';

const getPersonaCreatedSortValue = (persona) => {
  const profile = persona?.profile && typeof persona.profile === 'object' ? persona.profile : {};
  const timestamp = Date.parse(persona?.created_at || persona?.createdAt || profile.created_at || profile.createdAt || '');
  if (Number.isFinite(timestamp)) return timestamp;
  const idValue = Number(persona?.id);
  return Number.isFinite(idValue) ? idValue : 0;
};

const sortPersonasByCreatedAt = (personas = []) => (
  [...personas].sort((left, right) => getPersonaCreatedSortValue(right) - getPersonaCreatedSortValue(left))
);

const mergePersonasById = (...personaLists) => {
  const merged = [];
  const seenIds = new Set();

  personaLists.flat().forEach((persona) => {
    if (!persona) return;
    if (persona.id === null || persona.id === undefined || persona.id === '') {
      merged.push(persona);
      return;
    }

    const key = String(persona.id);
    if (seenIds.has(key)) return;
    seenIds.add(key);
    merged.push(persona);
  });

  return merged;
};

const selectHomePersonas = (personas = [], defaultFolder = null) => {
  const sortedPersonas = sortPersonasByCreatedAt(personas);
  if (!defaultFolder?.id) return sortedPersonas.slice(0, HOME_PERSONA_LIMIT);

  const defaultFolderId = String(defaultFolder.id);
  const defaultFolderPersonas = [];
  const otherPersonas = [];

  sortedPersonas.forEach((persona) => {
    if (String(getPersonaFolderId(persona)) === defaultFolderId) {
      defaultFolderPersonas.push(persona);
      return;
    }
    otherPersonas.push(persona);
  });

  return [...defaultFolderPersonas, ...otherPersonas].slice(0, HOME_PERSONA_LIMIT);
};

const pickPersonaTags = (persona = {}) => {
  const profile = persona.profile && typeof persona.profile === 'object' ? persona.profile : {};
  const telecom = persona.telecom_profile && typeof persona.telecom_profile === 'object'
    ? persona.telecom_profile
    : profile.telecom_profile && typeof profile.telecom_profile === 'object'
      ? profile.telecom_profile
      : {};
  const source =
    persona.tags ||
    persona.keywords ||
    persona.segment_tags ||
    profile.tags ||
    profile.keywords ||
    [];

  const tags = [];
  const addTag = (value) => {
    const normalized = normalizePersonaTag(value);
    if (!normalized || isExcludedHomePersonaTag(normalized) || tags.includes(normalized)) return;
    tags.push(normalized);
  };

  addTag(persona.tag || profile.tag);

  if (Array.isArray(source)) {
    source.forEach(addTag);
  }

  addTag(telecom.bundle_dependency_tendency || telecom.bundleDependencyTendency);
  addTag(telecom.bundle_dependency || telecom.bundleDependency);
  addTag(telecom.digital_literacy || telecom.digitalLiteracy);

  return tags.slice(0, 3);
};

const normalizeHomePersona = (persona = {}) => {
  const profile = persona.profile && typeof persona.profile === 'object' ? persona.profile : {};
  const name = persona.name || profile.name || '이름 없음';
  const gender = persona.gender || profile.gender || '';
  const ageValue = persona.age || profile.age || '';
  const age = ageValue ? `${String(ageValue).replace(/세$/, '')}세` : '';
  const role = persona.title || persona.roleArea || persona.role_area || persona.job || profile.job || profile.role || '';
  const quote =
    persona.quote ||
    profile.quote ||
    persona.one_liner ||
    persona.summary ||
    persona.personality ||
    profile.personality ||
    '저의 관점에서 검증 결과를 확인해보세요.';

  return {
    id: persona.id,
    quote,
    name,
    meta: [gender, age, role].filter(Boolean).join(' · ') || '-',
    avatar: getInitials(name),
    imageUrl: personaAssetUrl(persona.imageUrl || persona.image_url || profile.imageUrl || profile.image_url || ''),
    tags: pickPersonaTags(persona),
    createdAt: persona.created_at || persona.createdAt || profile.created_at || profile.createdAt || '',
  };
};

function UsageTooltipIcon({ label, children }) {
  return (
    <span className="b2b-home-tooltip">
      <button type="button" className="b2b-home-tooltip-icon" aria-label={label}>
        !
      </button>
      <span className="b2b-home-tooltip-bubble" role="tooltip">
        <span className="b2b-home-tooltip-copy">{children}</span>
        <span className="b2b-home-tooltip-close" aria-hidden="true">×</span>
      </span>
    </span>
  );
}

function HomePersonaAvatar({ persona }) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [persona.imageUrl]);

  return (
    <div className="b2b-home-avatar">
      {persona.imageUrl && !imageFailed ? (
        <img src={persona.imageUrl} alt="" onError={() => setImageFailed(true)} />
      ) : persona.avatar}
    </div>
  );
}

const B2BHomePage = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn, sessionChecked, isEnterprise } = useAppContext();
  const [authChecked, setAuthChecked] = useState(false);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [showNewStudyModal, setShowNewStudyModal] = useState(false);
  const [artifactEntryKind, setArtifactEntryKind] = useState(null); // 'guideline' | 'survey' | null
  const [homeLoading, setHomeLoading] = useState(true);
  const [homeError, setHomeError] = useState('');
  const [membershipUsage, setMembershipUsage] = useState(null);
  const [homePersonas, setHomePersonas] = useState([]);
  const [researchTests, setResearchTests] = useState([]);
  const [figmaStatus, setFigmaStatus] = useState(null);
  const personaPrevRef = useRef(null);
  const personaNextRef = useRef(null);
  const [retryingRows, setRetryingRows] = useState(new Set());
  const usageRange = useMemo(() => getThisMonthRange(), []);

  const announcementConfig = {
    title: 'ReOpsAI 업데이트',
    content: `<p>안녕하세요. ReOpsAI 운영진입니다.</p>
<p>ReOpsAI에 아래와 같은 새로운 기능이 추가되었습니다.</p>
<ul>
  <li>AI 퍼소나 생성</li>
  <li>AI 퍼소나 기반 UX Test</li>
</ul>
<p>많은 관심 부탁드립니다.</p>
<p class="announcement-email">오류 제보 및 개선사항 건의: <a href="mailto:ReOpsAI@pxd.co.kr">ReOpsAI@pxd.co.kr</a></p>`,
    showDontShowAgain: true,
  };

  useEffect(() => {
    if (!sessionChecked) return;
    if (!isLoggedIn) {
      navigate('/individual/login?session_expired=true');
      return;
    }
    setAuthChecked(true);
  }, [isLoggedIn, sessionChecked, navigate]);

  const loadHomeData = useCallback(async () => {
    setHomeLoading(true);
    setHomeError('');

    try {
      const requests = [
        getB2BMembershipUsage({ period: 'daily', ...usageRange }),
        personaApi.listPersonas({ limit: HOME_PERSONA_FETCH_LIMIT }),
        personaApi.listFolders(),
        personaTestApi.listTests(),
        personaAbTestApi.listTests(),
        personaInterviewApi.listInterviews(),
        personaFigmaApi.getStatus(),
      ];

      const results = await Promise.allSettled(requests);
      const [
        usageResult,
        personaResult,
        folderResult,
        uiTestResult,
        abTestResult,
        interviewResult,
        figmaResult,
      ] = results;

      const rejected = results.filter((result) => result.status === 'rejected');

      if (usageResult.status === 'fulfilled') {
        setMembershipUsage(usageResult.value?.success === false ? null : usageResult.value);
      }

      if (personaResult.status === 'fulfilled') {
        const folders = folderResult.status === 'fulfilled' ? folderResult.value : [];
        const defaultFolder = folders.find(isDefaultPersonaFolder) || null;
        let defaultFolderPersonas = [];

        if (defaultFolder?.id) {
          try {
            const defaultPersonaResult = await personaApi.listPersonas({
              limit: HOME_PERSONA_FETCH_LIMIT,
              folder_id: defaultFolder.id,
            });
            defaultFolderPersonas = defaultPersonaResult.items || [];
          } catch {
            defaultFolderPersonas = [];
          }
        }

        setHomePersonas(
          selectHomePersonas(
            mergePersonasById(defaultFolderPersonas, personaResult.value?.items || []),
            defaultFolder
          )
            .map(normalizeHomePersona)
        );
      }

      const uiTests = uiTestResult.status === 'fulfilled' ? uiTestResult.value : [];
      const abTests = abTestResult.status === 'fulfilled' ? abTestResult.value : [];
      const interviews = interviewResult.status === 'fulfilled' ? interviewResult.value : [];
      const hasCombinedKinds = uiTests.some((row) => row.kind);
      const combinedTests = hasCombinedKinds
        ? uiTests
        : [
            ...uiTests,
            ...abTests.map((row) => ({ ...row, kind: 'ab-test' })),
            ...interviews.map((row) => ({ ...row, kind: 'interview' })),
          ];
      setResearchTests(combinedTests);

      if (figmaResult.status === 'fulfilled') {
        setFigmaStatus(figmaResult.value);
      }

      if (rejected.length > 0) {
        setHomeError('일부 홈 데이터를 불러오지 못했습니다. 잠시 후 새로고침해 주세요.');
      }
    } catch (error) {
      setHomeError(error.message || '홈 데이터를 불러오지 못했습니다.');
    } finally {
      setHomeLoading(false);
    }
  }, [usageRange]);

  useEffect(() => {
    if (!authChecked) return;
    loadHomeData();
  }, [authChecked, loadHomeData]);

  useEffect(() => {
    if (!isLoggedIn || isEnterprise) {
      sessionStorage.removeItem(INDIVIDUAL_ANNOUNCEMENT_SHOWN_KEY);
      setAnnouncementModalOpen(false);
      return;
    }

    const isHidden = localStorage.getItem(INDIVIDUAL_ANNOUNCEMENT_HIDDEN_KEY) === 'true';
    const hasShown = sessionStorage.getItem(INDIVIDUAL_ANNOUNCEMENT_SHOWN_KEY) === 'true';

    if (!isHidden && !hasShown) {
      setAnnouncementModalOpen(true);
      sessionStorage.setItem(INDIVIDUAL_ANNOUNCEMENT_SHOWN_KEY, 'true');
    }
  }, [isLoggedIn, isEnterprise]);

  const handleAnnouncementModalClose = (dontShowAgain) => {
    if (dontShowAgain) {
      localStorage.setItem(INDIVIDUAL_ANNOUNCEMENT_HIDDEN_KEY, 'true');
    }
    setAnnouncementModalOpen(false);
  };

  usePageHeader({
    title: '',
    showBack: false,
  });

  const tokenStats = useMemo(() => {
    const tokenBalance = membershipUsage?.token_balance || membershipUsage?.balance || {};
    const totals = membershipUsage?.totals || membershipUsage?.usage?.totals || {};
    const granted = Number(
      membershipUsage?.granted_weighted_tokens ??
      tokenBalance.granted_weighted_tokens ??
      tokenBalance.total_weighted_tokens ??
      membershipUsage?.limit_weighted_tokens ??
      0
    );
    const used = Number(
      membershipUsage?.used_weighted_tokens ??
      tokenBalance.used_weighted_tokens ??
      totals.billable_weighted_tokens ??
      0
    );
    const remaining = Number(
      tokenBalance.remaining_weighted_tokens ??
      membershipUsage?.remaining_weighted_tokens ??
      (granted > 0 ? granted - used : 0)
    );
    const displayUsed = granted > 0 ? Math.min(used, granted) : used;
    const percent = granted > 0 ? Math.min(Math.round((displayUsed / granted) * 100), 100) : 0;
    const isDepleted = granted > 0 && (remaining <= 0 || used >= granted);

    return {
      granted,
      used,
      displayUsed,
      remaining,
      percent,
      isDepleted,
      isNearLimit: isDepleted || (granted > 0 && percent >= 90),
    };
  }, [membershipUsage]);

  const researchRows = useMemo(() => {
    return researchTests
      .map((row) => {
        const testType = getRowTestType(row);
        return {
          ...row,
          testType,
          rowKey: `${testType}:${row.id}`,
          displayType: getRowDisplayType(row, testType),
          personaCount: row.persona_count ?? row.personaCount ?? row.persona_ids?.length ?? row.personaIds?.length ?? row.context_data?.persona_ids?.length ?? 0,
          createdAt: row.created_at || row.createdAt,
        };
      })
      .sort((a, b) => {
        const aTime = new Date(a.createdAt || 0).getTime();
        const bTime = new Date(b.createdAt || 0).getTime();
        return bTime - aTime;
      });
  }, [researchTests]);

  const recentResearchRows = useMemo(() => (
    researchRows.slice(0, RECENT_RESEARCH_LIMIT)
  ), [researchRows]);

  const isFigmaConnected = Boolean(figmaStatus?.connected || figmaStatus?.account);
  const figmaAccount = figmaStatus?.account || {};
  const figmaAccountLabel =
    figmaAccount.figma_email ||
    figmaAccount.email ||
    figmaAccount.figma_handle ||
    figmaAccount.name ||
    user?.email ||
    '';
  const figmaConnectionLabel = figmaStatus ? (isFigmaConnected ? '연동완료' : '미연동') : homeLoading ? '확인중' : '확인불가';

  const openResearchRow = (row) => {
    if (row.testType === 'ab') {
      navigate(`/tests/ab/${row.id}`);
      return;
    }
    if (row.testType === 'interview') {
      navigate(`/tests/interview/${row.id}`);
      return;
    }
    navigate(`/tests/ui/${row.id}`);
  };

  const handleResearchAction = async (row) => {
    const status = normalizeStatus(row.status);
    if (status === 'completed') {
      openResearchRow(row);
      return;
    }
    if (status !== 'failed' || retryingRows.has(row.rowKey)) return;

    setRetryingRows((current) => {
      const next = new Set(current);
      next.add(row.rowKey);
      return next;
    });
    setHomeError('');
    try {
      if (row.testType === 'ab') {
        await personaAbTestApi.runTest(row.id);
      } else if (row.testType === 'interview') {
        await personaInterviewApi.runInterview(row.id);
      } else {
        await personaTestApi.runTest(row.id);
      }
      await loadHomeData();
    } catch (error) {
      setHomeError(error.message || '테스트를 재시도하지 못했습니다.');
    } finally {
      setRetryingRows((current) => {
        const next = new Set(current);
        next.delete(row.rowKey);
        return next;
      });
    }
  };

  if (!authChecked) {
    return (
      <B2BLayout hideCollapseButton={true} defaultCollapsed={false} hidePageHeader={true}>
        <div className="b2b-home-page">
          <div className="b2b-home-shell">
            <div className="b2b-home-loading">로딩 중...</div>
          </div>
        </div>
      </B2BLayout>
    );
  }

  return (
    <B2BLayout hideCollapseButton={true} defaultCollapsed={false} hidePageHeader={true}>
      <div className="b2b-home-page">
        <main className="b2b-home-shell" aria-label="B2B 홈">
          <section className="b2b-home-hero">
            <div className="b2b-home-hero-copy">
              <p className="b2b-home-eyebrow">환영합니다 {user?.name || '박안나'} 님,</p>
              <h1>복잡한 과정은 AI에게 맡기고 가장 중요한 발견에만 집중하세요.</h1>
            </div>
            <div className="b2b-home-hero-art" aria-hidden="true">
              <span className="hero-shape hero-shape--green" />
              <span className="hero-shape hero-shape--rose" />
              <span className="hero-shape hero-shape--blue" />
            </div>
          </section>

          <section className="b2b-home-usage" aria-label="사용 현황">
            <div className="b2b-home-usage-item b2b-home-usage-item--wide">
              <div className="b2b-home-usage-label">
                {isEnterprise ? '사용 토큰' : '체험 토큰'}
                <UsageTooltipIcon label={isEnterprise ? '사용 토큰 안내' : '체험 토큰 안내'}>
                  {isEnterprise ? (
                    <>
                      <span>조직 전체에 할당된 공용 토큰입니다.</span>
                      <span>충전 및 관리는 관리자에게 요청해주세요.</span>
                    </>
                  ) : (
                    <>
                      <span>무료 체험으로 제공된 토큰입니다.</span>
                      <span>소진 후에는 이용 문의를 통해 계속 이용하실 수 있습니다.</span>
                    </>
                  )}
                </UsageTooltipIcon>
              </div>
              <div className={`b2b-home-token-value ${tokenStats.isNearLimit ? 'is-near-limit' : ''}`}>
                {formatNumber(tokenStats.displayUsed)} / {formatNumber(tokenStats.granted)}
              </div>
              <div className="b2b-home-token-track" aria-hidden="true">
                <span className={tokenStats.isNearLimit ? 'is-near-limit' : ''} style={{ width: `${tokenStats.percent}%` }} />
              </div>
              {!isEnterprise && tokenStats.isDepleted ? (
                <div className="b2b-home-token-depleted">
                  <p className="b2b-home-token-depleted__text">체험 토큰을 모두 사용했습니다.</p>
                  <button
                    type="button"
                    className="b2b-home-token-depleted__action"
                    onClick={() => navigate('/inquiry')}
                  >
                    이용 문의
                  </button>
                </div>
              ) : null}
            </div>
            <div className="b2b-home-usage-item">
              <button
                type="button"
                className="b2b-home-usage-label b2b-home-usage-link"
                onClick={() => navigate('/tests')}
              >
                생성한 리서치
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <div className="b2b-home-usage-value">
                {formatNumber(researchRows.length)}건
              </div>
            </div>
            <div className="b2b-home-usage-item">
              <div className="b2b-home-usage-label">
                Figma 연동여부
                <UsageTooltipIcon label="Figma 연동 안내">
                  Figma를 연동하면 디자인 파일을 불러와 화면 기반의 사용성 검증을 바로 시작할 수 있습니다.
                </UsageTooltipIcon>
              </div>
              <div className="b2b-home-figma-value">
                {isFigmaConnected && figmaAccountLabel ? (
                  <span className="b2b-home-figma-account">{figmaAccountLabel}</span>
                ) : null}
                <button
                  type="button"
                  className={`b2b-home-status-pill ${isFigmaConnected ? 'is-connected' : figmaStatus ? 'is-disconnected' : 'is-unknown'}`}
                  onClick={() => navigate('/b2b/settings/figma')}
                >
                  {figmaConnectionLabel}
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          </section>

          {homeError ? <div className="b2b-home-error" role="status">{homeError}</div> : null}

          <section className="b2b-home-section">
            <div className="b2b-home-section-header">
              <h2>검증 목적에 최적화된 AI 퍼소나를 만나보세요.</h2>
              <div className="b2b-home-carousel-controls">
                <button
                  type="button"
                  ref={personaPrevRef}
                  className="b2b-home-persona-nav-prev"
                  disabled={homePersonas.length <= PERSONA_PAGE_SIZE}
                  aria-label="이전 퍼소나"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <button
                  type="button"
                  ref={personaNextRef}
                  className="b2b-home-persona-nav-next"
                  disabled={homePersonas.length <= PERSONA_PAGE_SIZE}
                  aria-label="다음 퍼소나"
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            </div>

            <div className="b2b-home-persona-grid">
              {homeLoading ? (
                <article className="b2b-home-persona-card b2b-home-persona-empty b2b-home-persona-swiper">
                  AI 퍼소나를 불러오고 있습니다.
                </article>
              ) : homePersonas.length === 0 ? (
                <article className="b2b-home-persona-card b2b-home-persona-empty b2b-home-persona-swiper">
                  생성된 AI 퍼소나가 없습니다.
                </article>
              ) : (
                <Swiper
                  className="b2b-home-persona-swiper"
                  modules={[Navigation]}
                  slidesPerView={3}
                  slidesPerGroup={1}
                  spaceBetween={8}
                  speed={420}
                  loop={homePersonas.length > PERSONA_PAGE_SIZE}
                  navigation={{
                    prevEl: personaPrevRef.current,
                    nextEl: personaNextRef.current,
                  }}
                  onBeforeInit={(swiper) => {
                    swiper.params.navigation.prevEl = personaPrevRef.current;
                    swiper.params.navigation.nextEl = personaNextRef.current;
                  }}
                  breakpoints={{
                    0: {
                      slidesPerView: 1.08,
                      spaceBetween: 8,
                    },
                    640: {
                      slidesPerView: 2,
                      spaceBetween: 8,
                    },
                    1024: {
                      slidesPerView: 3,
                      spaceBetween: 8,
                    },
                  }}
                >
                  {homePersonas.map((persona) => (
                    <SwiperSlide key={persona.id || persona.name}>
                      <article className="b2b-home-persona-card">
                        <div className="b2b-home-persona-quote">
                          <p>“{persona.quote}”</p>
                        </div>
                        <div className="b2b-home-persona-body">
                          <div className="b2b-home-persona-profile">
                            <HomePersonaAvatar persona={persona} />
                            <h3>{persona.name}</h3>
                            <p>{persona.meta}</p>
                          </div>
                          <div className="b2b-home-persona-footer">
                          {persona.tags.length > 0 ? (
                            <div className="b2b-home-persona-tags">
                              {persona.tags.map((tag, index) => (
                                index === 0 ? (
                                  <PersonaTagChip key={tag} tag={tag} />
                                ) : (
                                  <span key={tag} title={tag}>{formatPersonaTagLabel(tag)}</span>
                                )
                              ))}
                            </div>
                          ) : <span />}
                          <button
                            type="button"
                            className="b2b-home-card-action"
                            aria-label={`${persona.name} 상세 보기`}
                            onClick={() => navigate(`/personas/${persona.id}`)}
                            disabled={!persona.id}
                          >
                            <FontAwesomeIcon icon={faArrowRight} />
                          </button>
                        </div>
                        </div>
                      </article>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}

              <article className="b2b-home-persona-card b2b-home-persona-card--new">
                <h3>새로운<br />AI 퍼소나가<br />필요하다면?</h3>
                <button
                  type="button"
                  className="b2b-home-disabled-button"
                  onClick={() => navigate('/personas/new')}
                >
                  퍼소나 생성하기
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </article>
            </div>
          </section>

          <section className="b2b-home-section">
            <h2>생성된 AI 퍼소나를 활용해 빠르게 검증해보세요.</h2>
            <div className="b2b-home-validation-grid">
              {validationMethods.map((method) => (
                <button
                  type="button"
                  className={`b2b-home-validation-card b2b-home-validation-card--${method.tone} ${
                    method.featured ? 'b2b-home-validation-card--featured' : ''
                  }`}
                  key={method.id}
                  onClick={() => navigate(method.path)}
                >
                  <span className="b2b-home-validation-icon">
                    <FontAwesomeIcon icon={method.icon} />
                  </span>
                  <span className="b2b-home-validation-copy">
                    <strong>{method.title}</strong>
                    <span>{method.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="b2b-home-research-band">
            <div className="b2b-home-section b2b-home-research-section">
              <h2>생성한 리서치 목록</h2>
              <div className="b2b-home-table-wrap">
                <table className="b2b-home-table">
                  <thead>
                    <tr>
                      <th>테스트 명</th>
                      <th>테스트 유형</th>
                      <th>참여 퍼소나 수</th>
                      <th>생성일자</th>
                      <th>진행 상태</th>
                      <th>상세</th>
                    </tr>
                  </thead>
                  <tbody>
                    {homeLoading ? (
                      <tr>
                        <td colSpan={6} className="b2b-home-table-empty">리서치 목록을 불러오고 있습니다.</td>
                      </tr>
                    ) : researchRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="b2b-home-table-empty">생성한 리서치가 없습니다.</td>
                      </tr>
                    ) : recentResearchRows.map((row) => {
                      const isRetrying = retryingRows.has(row.rowKey);
                      const status = isRetrying ? 'running' : normalizeStatus(row.status);
                      const actionLabel = isRetrying ? '생성 중' : status === 'failed' ? '재시도' : '결과보기';
                      return (
                      <tr key={row.rowKey}>
                        <td>{row.name || '-'}</td>
                        <td>{row.displayType}</td>
                        <td>
                          <span className="b2b-home-persona-count">
                            <FontAwesomeIcon icon={faUserCircle} />
                            {row.personaCount}명
                          </span>
                        </td>
                        <td>{formatTestDate(row.createdAt)}</td>
                        <td>
                          <span className={`b2b-home-result-pill is-${status}`}>
                            {testStatusLabel(status)}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="b2b-home-table-button"
                            onClick={() => handleResearchAction(row)}
                            disabled={status === 'running'}
                          >
                            {actionLabel}
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="b2b-home-section b2b-home-research-create-section">
            <h2>리서치 준비, 클릭 한 번으로 해보세요.</h2>
            <div className="b2b-home-research-create-grid">
              {researchCreateMethods.map((method) => (
                <button
                  type="button"
                  className={`b2b-home-research-create-card b2b-home-research-create-card--${method.id}`}
                  key={method.id}
                  onClick={() => {
                    if (method.id === 'plan') {
                      setShowNewStudyModal(true);
                      return;
                    }
                    setArtifactEntryKind(method.id);
                  }}
                >
                  <span className="b2b-home-research-create-icon">
                    <img src={method.icon} alt="" aria-hidden="true" />
                  </span>
                  <span className="b2b-home-research-create-copy">
                    <strong>{method.title}</strong>
                    <span>{method.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        </main>

        <AnnouncementModal
          isOpen={announcementModalOpen}
          onRequestClose={handleAnnouncementModalClose}
          title={announcementConfig.title}
          content={announcementConfig.content}
          showDontShowAgain={announcementConfig.showDontShowAgain}
        />
        <NewStudyModal
          isOpen={showNewStudyModal}
          onClose={() => setShowNewStudyModal(false)}
        />
        <ArtifactEntryModal
          isOpen={Boolean(artifactEntryKind)}
          artifactKind={artifactEntryKind || 'guideline'}
          onClose={() => setArtifactEntryKind(null)}
        />
      </div>
    </B2BLayout>
  );
};

export default B2BHomePage;
